<template>
  <div class="ft-report-table-wrap">
  <!--
    Summary layout: a label/value stack, not a table. rptInvtSum has an
    EMPTY detail band (its four figures are header variables beside header
    labels, rptInvtSum.dfm:692-755), so there is no column list, no group
    break and no total. Rendering it through the table path below would draw
    a one-row grid with a footer repeating that row.
  -->
  <div class="card-body ft-report" v-if="summary">
    <h4 class="text-center mb-0">{{ headingText !== null ? headingText : (layout.heading || layout.title) }}</h4>
    <template v-if="subheadingLines.length">
      <p class="text-center text-muted small mb-0" v-for="(line, i) in subheadingLines" :key="i">{{ line }}</p>
    </template>
    <p class="text-center text-muted small" v-else>{{ summaryCaption }}</p>

    <table class="table table-sm ft-report-table ft-summary mx-auto">
      <tbody>
      <tr v-for="field in layout.fields" :key="field.key">
        <td>{{ field.label }}</td>
        <td class="text-right">{{ formatCell(figure(field.key), summaryColumn(field)) }}</td>
      </tr>
      </tbody>
    </table>
  </div>

  <div class="card-body ft-report" v-else>
    <!--
      The heading FrameTracker builds at run time (lblHead.Caption := ...)
      where the report declares one; otherwise the static title over the
      date range, worded "from X to Y" as FrameTracker words it.
    -->
    <template v-if="headingText !== null">
      <h4 class="text-center mb-0">{{ headingText }}</h4>
    </template>
    <template v-else>
      <h4 class="text-center mb-0">{{ layout.title }}</h4>
      <p class="text-center text-muted small mb-0" v-if="layout.subtitle">{{ layout.subtitle }}</p>
    </template>
    <p class="text-center text-muted small mb-0" v-for="(line, i) in subheadingLines" :key="'s' + i">{{ line }}</p>
    <p class="text-center text-muted small" v-if="headingText === null && rangeCaption">{{ rangeCaption }}</p>
    <div class="mb-2" v-else></div>

    <div class="ft-pager text-center small mb-2 no-print" v-if="reportPages.length > 1 && !printAll">
      <button class="btn btn-xs btn-default" :disabled="page === 0" @click="page = 0">First</button>
      <button class="btn btn-xs btn-default" :disabled="page === 0" @click="page--">Prev</button>
      <span class="mx-2">Page {{ page + 1 }} of {{ reportPages.length }} ({{ rows.length.toLocaleString('en-US') }} lines)</span>
      <button class="btn btn-xs btn-default" :disabled="isLastPage" @click="page++">Next</button>
      <button class="btn btn-xs btn-default" :disabled="isLastPage" @click="page = reportPages.length - 1">Last</button>
    </div>

    <table class="table table-sm ft-report-table">
      <thead>
      <tr>
        <th v-for="col in reportColumns" :key="col.key" :class="[alignFor(col), {'ft-sortable': sortable}]"
            :aria-sort="ariaSort(col)" :tabindex="sortable ? 0 : null" :title="sortable ? 'Click to sort' : null"
            @click="sortable && $emit('sort', col.key)"
            @keydown.enter.prevent="sortable && $emit('sort', col.key)">
          {{ col.label }}<span class="ft-sort-mark no-print" v-if="sortable && sortKey === col.key">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
        </th>
      </tr>
      </thead>

      <template v-for="(part, p) in pageParts" :key="part.key">
        <tbody :class="{'ft-break-before': p > 0 && breaksBefore(part)}">
        <!-- Outer group headers that begin with this section (groupLevels). -->
        <template v-if="part.first">
          <tr v-for="group in part.section.opens" :key="'open-' + group.key" class="ft-group-header ft-group-outer">
            <td :colspan="reportColumns.length">{{ groupHeader(group) }}</td>
          </tr>
        </template>

        <tr v-if="part.section.level" class="ft-group-header">
          <td :colspan="reportColumns.length">{{ groupHeader(part.section) }}{{ part.first ? '' : ' (continued)' }}</td>
        </tr>

        <tr v-for="(row, i) in part.rows" :key="i">
          <td v-for="col in reportColumns" :key="col.key" :class="alignFor(col)">
            <template v-if="col.key === 'Days' && layout.daysSentinel !== undefined">
              <!--
                rptInvDaily.pas:387-392: Days prints only when it is not the -1
                sentinel AND the balance is non-zero; a settled invoice shows
                PAID in its place instead.
              -->
              <span v-if="showPaid(row)" class="badge badge-secondary">{{ layout.paidLabel }}</span>
              <span v-else-if="showDays(row)">{{ row.Days }}</span>
            </template>
            <template v-else>
              {{ formatCell(row[col.key], col) }}<!--
                rptCheckRegister prints a bare 'm' after the payment when one
                check settled several invoices (ppvarMulCalc). Not a column of
                its own: it hangs off the last one.
              --><span
                  v-if="col.key === lastReportColumn && markerFor(row)"
                  class="ft-marker">{{ markerFor(row) }}</span>
            </template>
          </td>
        </tr>

        <template v-if="part.last">
          <!-- The innermost group's footer lines, then outer groups closing here, innermost first. -->
          <template v-for="group in footerGroups(part.section)" :key="'foot-' + group.key">
            <tr v-for="(line, f) in footerLines(group)" :key="f" class="ft-subtotal" :class="{'ft-subtotal-outer': group !== part.section}">
              <td v-for="cell in line" :key="cell.key" :colspan="cell.colspan" :class="cell.cls">{{ cell.text }}</td>
            </tr>
          </template>
        </template>
        </tbody>
      </template>

      <tfoot v-if="showTotalRow && isLastPage && hasResult">
      <!--
        Some summary bands print the total split in two rather than as one
        figure: rptCheckRegister separates Check Total from Term Discount on
        TranType (ppvarCheckCalc / ppvarTermCalc), with the check count beside
        them (ppDBCalc5).
      -->
      <!-- summaryRows with position 'beforeTotal'. -->
      <tr class="ft-total ft-summary-row" v-for="(cells, i) in summaryRowsBefore" :key="'sumb' + i">
        <td v-for="cell in cells" :key="cell.key" :colspan="cell.colspan" :class="cell.cls">{{ cell.text }}</td>
      </tr>
      <template v-if="splitTotalRows">
        <tr class="ft-total" v-for="(cells, i) in splitTotalRows" :key="'split' + i">
          <td v-for="cell in cells" :key="cell.key" :colspan="cell.colspan" :class="cell.cls">{{ cell.text }}</td>
        </tr>
      </template>

      <!--
        The row count is printed ONLY where the layout says: rptInvDaily's
        ppDBCalc8 lives in detail band 2, so ordering by anything else genuinely
        prints no count. See showCount and totalRow().
      -->
      <tr class="ft-total" v-else-if="hasTotals">
        <td v-for="cell in grandTotalCells" :key="cell.key" :colspan="cell.colspan" :class="cell.cls">
          {{ cell.text }}
        </td>
      </tr>

      <!-- Further summary-band lines after the total (summaryRows). -->
      <tr class="ft-total ft-summary-row" v-for="(cells, i) in summaryRowsAfter" :key="'sum' + i">
        <td v-for="cell in cells" :key="cell.key" :colspan="cell.colspan" :class="cell.cls">{{ cell.text }}</td>
      </tr>
      </tfoot>
    </table>

    <div class="ft-pager text-center small mt-2 no-print" v-if="reportPages.length > 1 && !printAll">
      <button class="btn btn-xs btn-default" :disabled="page === 0" @click="page--">Prev</button>
      <span class="mx-2">Page {{ page + 1 }} of {{ reportPages.length }}</span>
      <button class="btn btn-xs btn-default" :disabled="isLastPage" @click="page++">Next</button>
    </div>

    <!--
      Second datasets printed beneath the report (a subreport in
      FrameTracker): the card-brand summary, the taxcode summary. Each has
      its own columns, formats and total row, declared as sideTables.
    -->
    <template v-if="isLastPage">
      <div class="ft-side" v-for="(table, t) in sideTables" :key="'side' + t">
        <h6 class="ft-side-title" v-if="table.title">{{ table.title }}</h6>
        <table class="table table-sm ft-report-table">
          <thead>
          <tr>
            <th v-for="col in table.columns" :key="col.key" :class="sideAlign(table, col)">{{ col.label }}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(row, i) in table.rows" :key="i">
            <td v-for="col in table.columns" :key="col.key" :class="sideAlign(table, col)">
              {{ formatCell(row[col.key], col) }}
            </td>
          </tr>
          </tbody>
          <tfoot v-if="table.totals.length">
          <tr class="ft-total">
            <td v-for="(col, c) in table.columns" :key="col.key" :class="sideAlign(table, col)">
              <template v-if="table.totals.includes(col.key)">{{ formatTotal(sum(table.rows, col.key), col) }}</template>
              <template v-else-if="c === 0">{{ table.totalsCaption }}</template>
            </td>
          </tr>
          </tfoot>
        </table>
      </div>
    </template>

    <!--
      quRepCCcat, the card-brand breakdown printed beneath both charge-card
      layouts. Kept for layouts not yet moved onto sideTables.
    -->
    <table class="table table-sm ft-report-table ft-categories" v-if="categories.length && isLastPage">
      <thead>
      <tr>
        <th>{{ layout.categoryLabel || 'Location' }}</th>
        <th v-for="col in categoryColumns" :key="col.key" class="text-right">{{ col.label }}</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="(row, i) in categories" :key="i">
        <td>{{ row.Name || row.Loc }}</td>
        <td v-for="col in categoryColumns" :key="col.key" class="text-right">
          {{ money(row[col.key]) }}
        </td>
      </tr>
      </tbody>
    </table>
  </div>

    <!-- footerNotes printed after the report. -->
    <template v-if="isLastPage">
      <p class="ft-note small mb-1 px-3" v-for="(note, n) in endNotes" :key="'note' + n">{{ note.text }}</p>
    </template>
    <!-- footerNotes in the page footer (fixed, so print repeats them per page). -->
    <div class="ft-page-note small px-3" v-for="(note, n) in pageNotes" :key="'pnote' + n">{{ note.text }}</div>
  </div>
</template>

<script>
import {formatFloat, formatWith, dateToStr} from './delphiFormat'
import {partApplies, pathValue, renderLines, renderTemplate} from './reportTemplate'
import {NUMERIC_TYPES, alignFor, formatCell, money, price} from './reportCells'

/**
 * One printed FrameTracker layout: headings, column captions, group bands,
 * totals, summary lines, side tables and footer notes, over one set of rows.
 *
 * ReportRunner draws one of these per run, or several when a layout declares
 * `sections` (FrameTracker options that print two reports in one go). All
 * layout decisions read `ranWith`, the criteria of the run on screen, never
 * the live form.
 */

// Lines per page in the Report view. Enough to read like a printed page run,
// small enough to draw instantly.
const PAGE_LINES = 1000

export default {
  name: "ReportTable",
  props: {
    layout: {type: Object, required: true},
    rows: {type: Array, required: true},
    // The report's column definitions (response meta.columns, the catalog's, or
    // a section's columnDefs).
    columns: {type: Array, required: true},
    // Template context of the run: params, paramDefs, options, meta, user.
    context: {type: Object, required: true},
    meta: {type: Object, default: () => ({})},
    ranWith: {type: Object, default: () => ({})},
    // True while printing: every page is drawn, not one.
    printAll: {type: Boolean, default: false},
    // Header clicks emit 'sort'; the runner sorts the rows and drops the grouping.
    sortable: {type: Boolean, default: false},
    sortKey: {type: String, default: null},
    sortDir: {type: String, default: null},
  },
  emits: ['pages', 'sort'],
  data() {
    return {
      // Current page of the Report view; see reportPages.
      page: 0,
    }
  },
  computed: {
    hasResult() {
      return this.rows.length > 0
    },
    gridColumns() {
      return this.columns
    },
    columnsByKey() {
      const map = {}
      this.columns.forEach(c => {
        map[c.key] = c
      })
      return map
    },
    paramDefs() {
      return this.context.paramDefs || {}
    },
    /** The template context for these rows. */
    templateContext() {
      return {...this.context, columns: this.columnsByKey, rows: this.rows, meta: this.meta}
    },
    /** Whether the report currently has a category breakdown to print beneath it. */
    categories() {
      if (!this.hasResult || !this.layout || !this.layout.categoryBreakdown) return []
      return (this.meta && this.meta.categories) || []
    },
    /**
     * Columns of that breakdown, derived from the first row rather than declared.
     * Kept for layouts still using `categoryBreakdown`; `sideTables` declares its
     * columns in FrameTracker's order instead.
     */
    categoryColumns() {
      if (this.categories.length === 0) return []
      return Object.keys(this.categories[0])
          .filter(k => k !== 'Loc' && k !== 'Name')
          .map(k => ({key: k, label: k === 'PayTot' ? 'Total' : k, type: 'money'}))
    },
    /** Second datasets printed beneath the report, each with its rows. */
    sideTables() {
      if (!this.hasResult || !this.layout || !Array.isArray(this.layout.sideTables)) return []
      return this.layout.sideTables.filter(table => {
        if (!table.hideWhenEmpty) return true
        const rows = pathValue({meta: this.meta || {}}, table.source)
        return Array.isArray(rows) && rows.length > 0
      }).map(table => {
        const rows = pathValue({meta: this.meta || {}}, table.source)
        const list = Array.isArray(rows) ? rows : []
        // A column the server stripped (the user lacks its permission) is absent
        // from every row: leave it out rather than print an empty column.
        const columns = (table.columns || []).filter(c => !list.length || list.some(r => Object.prototype.hasOwnProperty.call(r, c.key)))
        return {
          title: table.title || '',
          columns,
          totals: table.totals || [],
          totalsCaption: table.totalsCaption || '',
          rows: list,
        }
      })
    },
    /**
     * Whether this report prints as a label/value stack rather than a table.
     *
     * A report with four scalars and no dataset (rptInvtSum is the first) has
     * nothing the table path can use: no columns, no grouping, no totals. The
     * server says so explicitly rather than this being inferred from a row count,
     * because one row is a perfectly ordinary result for a table report too.
     */
    summary() {
      return !!this.layout && this.layout.style === 'summary'
    },
    /**
     * 'From 6/1/2026 to 6/30/2026': the caption the legacy form builds with
     * DateToStr (rptInvtSum.pas:bbnPrntClick), in its own US format rather than
     * the ISO dates the grid header shows.
     */
    summaryCaption() {
      if (!this.meta) return ''
      const from = this.usDate(this.meta.date_from)
      const to = this.usDate(this.meta.date_to)
      if (!from || !to) return ''
      return 'From ' + from + ' to ' + to
    },
    /** The run-time heading (lblHead), or null where the layout declares none. */
    headingText() {
      if (!this.layout || this.layout.heading === undefined || this.layout.heading === null) return null
      return renderTemplate(this.layout.heading, this.templateContext)
    },
    subheadingLines() {
      if (!this.layout || !this.layout.subheadings) return []
      return renderLines(this.layout.subheadings, this.templateContext).filter(line => line !== '')
    },
    /**
     * The group breaks, outer to inner. `groupLevels` declares nested ones:
     * rptDeliveryAcct breaks on Location, then on Shipvia within it, each with its
     * own footer. A layout with a single break declares `groupBy` alone.
     */
    groupLevels() {
      if (!this.layout) return []
      if (Array.isArray(this.layout.groupLevels) && this.layout.groupLevels.length) {
        return this.layout.groupLevels
      }
      return this.layout.groupBy ? [this.layout.groupBy] : []
    },
    /**
     * Whether the grouped (Location-ordered) legacy layout applies, judged on the
     * run on screen. Reading the live form here redrew old rows in the other
     * layout as soon as Report Order was changed (Daily Activity).
     */
    grouped() {
      if (!this.groupLevels.length) return false
      const when = this.groupLevels[0].when || {}
      return Object.keys(when).every(k => this.ranMatches(k, when[k]))
    },
    reportColumns() {
      if (!this.layout) return this.gridColumns
      const keys = (this.grouped && this.layout.groupedColumns
          ? this.layout.groupedColumns
          : this.layout.columns) || []
      // hideColumns: [{columns, when | whenBlank}], columns FrameTracker hides for
      // a run (Daily Activity drops Company once one account is picked).
      const hidden = new Set()
      ;(this.layout.hideColumns || []).forEach(rule => {
        if (partApplies(rule, this.ranWith, this.meta || {}, this.paramDefs)) (rule.columns || []).forEach(k => hidden.add(k))
      })
      return keys.filter(k => !hidden.has(k)).map(k => this.columnsByKey[k] || {key: k, label: k, type: 'string'})
    },
    totalKeys() {
      return (this.layout && this.layout.totals) || []
    },
    /** Key of the final printed column, where a row marker is appended. */
    lastReportColumn() {
      const columns = this.reportColumns
      return columns.length ? columns[columns.length - 1].key : null
    },
    /**
     * Whether a row count is printed.
     *
     * 'grouped' means only the grouped layout prints one: rptInvDaily's dcCount
     * lives in detail band 2 alone, which is why validating it in Date order could
     * never confirm a count. 'always' means both bands carry one, as
     * rptCheckRegister's check detail layout does. A layout naming the count's
     * caption or label but no countIn prints it always.
     */
    showCount() {
      if (!this.layout) return false
      if (this.layout.countIn === 'always') return true
      if (this.layout.countIn === 'grouped') return this.grouped
      if (this.layout.countIn === undefined || this.layout.countIn === null) {
        return !!(this.layout.countCaption || this.layout.countLabel)
      }
      return false
    },
    /**
     * The count as the summary band prints it: countLabel, the number, then
     * countCaption ('Total: 123 Records'), placed under `countAt` when given.
     */
    countSpec() {
      if (!this.layout) return null
      return {
        label: this.layout.countLabel || '',
        caption: this.layout.countCaption || '',
        at: this.layout.countAt || null,
        format: this.layout.countFormat || null,
      }
    },
    /** Margin columns of the total rows: {Column: {cost, price} | {profit, total}}. */
    totalMargins() {
      return (this.layout && this.layout.totalMargins) || {}
    },
    /**
     * How many leading columns a totals caption spans: everything before the
     * first column carrying a figure in any total row.
     */
    labelSpan() {
      const keys = new Set([
        ...this.totalKeys,
        ...((this.layout && this.layout.groupTotals) || []),
        ...this.groupLevels.reduce((all, level) => all.concat(level.totals || []), []),
        ...Object.keys(this.totalMargins),
      ])
      const idx = this.reportColumns.findIndex(c => keys.has(c.key))
      return idx > 0 ? idx : 1
    },
    /**
     * Rows split into printed sections: one per innermost group when grouped,
     * otherwise a single unlabeled section. Grouping relies on the server having
     * already sorted by the group keys, so this only has to detect the breaks.
     *
     * With nested levels each section also lists the outer groups that open at its
     * start (`opens`, outer first) and close after it (`closes`, inner first), each
     * carrying its own rows for its footer: the outer header and Location Total
     * bands of rptDeliveryAcct. A single level leaves both empty. Each group keeps
     * its level, whose header / footer / count / totals it prints with.
     */
    reportSections() {
      if (!this.grouped) {
        return [{key: 'all', level: null, rows: this.rows, opens: [], closes: []}]
      }

      const levels = this.groupLevels
      const inner = levels[levels.length - 1]
      const outerCount = levels.length - 1
      const sections = []
      const open = []
      let current = null
      let previous = null

      this.rows.forEach(row => {
        const keys = levels.map(level => row[level.key])
        // The outermost level whose key changed; everything inside it breaks too.
        const changed = previous === null ? 0 : keys.findIndex((k, i) => k !== previous[i])

        if (changed !== -1) {
          for (let i = outerCount - 1; i >= changed; i--) {
            if (open[i]) current.closes.push(open[i])
          }
          current = {key: keys.join('\u0000'), level: inner, rows: [], opens: [], closes: []}
          for (let i = changed; i < outerCount; i++) {
            open[i] = {key: keys.slice(0, i + 1).join('\u0000'), level: levels[i], rows: []}
            current.opens.push(open[i])
          }
          sections.push(current)
        }

        current.rows.push(row)
        for (let i = 0; i < outerCount; i++) open[i].rows.push(row)
        previous = keys
      })

      if (current) {
        for (let i = outerCount - 1; i >= 0; i--) current.closes.push(open[i])
      }

      return sections
    },
    /**
     * Whether the grand total row has anything to print. Vendor Listing and PO
     * Detail declare no totals at all; drawing their rows anyway left an empty
     * captionless line under every group and at the foot.
     */
    hasTotals() {
      return this.totalKeys.length > 0 || this.showCount || Object.keys(this.totalMargins).length > 0
    },
    /**
     * Whether the summary band prints. `hideTotalsWhen: {param: '*'}` hides it once
     * that parameter is set: rptInvtType hides its summary band when one type is
     * picked, rptDeliveryAcct when one location is, because the single group's
     * subtotal would only repeat it.
     */
    showTotalRow() {
      if (!this.hasTotals && !this.summaryRowCells.length) return false
      return !this.hiddenBy(this.layout && this.layout.hideTotalsWhen)
    },
    grandTotalCells() {
      return this.totalRow(this.rows, {
        caption: this.layout.totalsCaption || '',
        totals: this.totalKeys,
        count: this.showCount ? this.countSpec : null,
      })
    },
    /**
     * A total split in two by the value of one column: rptCheckRegister's
     * summary band prints Check Total and Term Discount separately rather than
     * one figure (ppvarCheckCalc / ppvarTermCalc), split on TranType, and the
     * check count (ppDBCalc5) beside the first. Null when not declared.
     */
    splitTotalRows() {
      const split = this.layout && this.layout.splitTotal
      if (!split || !this.totalKeys.length) return null
      const key = this.totalKeys[0]
      const matched = this.rows.filter(r => r[split.key] === split.value)
      const rest = this.rows.filter(r => r[split.key] !== split.value)
      return [
        this.totalRow(rest, {caption: split.restLabel, totals: [key], count: this.showCount ? this.countSpec : null, countRows: this.rows, margins: false}),
        this.totalRow(matched, {caption: split.matchLabel, totals: [key], margins: false}),
      ]
    },
    /**
     * Further summary-band lines (Check Register's "Credit Card Total", the
     * Monthly report's unlabeled sums). Each is {label, cells: {Column: spec}},
     * a spec being {sum: key, where?: {col: [values]}}, {value: 'meta.x'},
     * {count: true} or {text}.
     */
    summaryRowCells() {
      const lines = (this.layout && this.layout.summaryRows) || []
      return lines.map(line => {
        const texts = this.reportColumns.map(col => this.summaryCell((line.cells || {})[col.key], col))
        const cells = this.captionRow(renderTemplate(line.label || '', this.templateContext), texts)
        cells.position = line.position === 'beforeTotal' ? 'beforeTotal' : 'afterTotal'
        return cells
      })
    },
    summaryRowsBefore() {
      return this.summaryRowCells.filter(c => c.position === 'beforeTotal')
    },
    summaryRowsAfter() {
      return this.summaryRowCells.filter(c => c.position !== 'beforeTotal')
    },
    /**
     * 'from 6/1/2026 to 6/30/2026', FrameTracker's wording and DateToStr dates,
     * for a layout with no run-time heading of its own. One date alone when the
     * range is a single day.
     */
    rangeCaption() {
      if (!this.meta) return ''
      const from = this.usDate(this.meta.date_from)
      const to = this.usDate(this.meta.date_to)
      if (!from && !to) return ''
      if (!from || !to || from === to) return from || to
      return 'from ' + from + ' to ' + to
    },
    /**
     * The Report view, cut into pages of about PAGE_LINES lines, like a print
     * preview. Drawing every line at once froze the browser: the default Product
     * Listing is 21,000 lines, and with Both and zero stock 48,000 (over half a
     * million table cells). The Grid view pages itself through DataTables.
     *
     * A page holds whole sections where it can; a section bigger than a page is
     * split, and its pieces repeat the group heading marked "(continued)". Group
     * headers print with a section's first piece and subtotals with its last, each
     * still summing the whole group. The grand total prints on the last page and
     * covers every line.
     */
    reportPages() {
      const pages = []
      let current = []
      let lines = 0

      const flush = () => {
        if (current.length) pages.push(current)
        current = []
        lines = 0
      }

      this.reportSections.forEach(section => {
        const total = section.rows.length
        let start = 0

        do {
          if (lines >= PAGE_LINES) flush()
          const take = Math.max(1, Math.min(total - start, PAGE_LINES - lines))
          current.push({
            key: section.key + '\u0000' + start,
            section: section,
            rows: section.rows.slice(start, start + take),
            first: start === 0,
            last: start + take >= total,
          })
          lines += take
          start += take
        } while (start < total)
      })

      flush()
      return pages
    },
    pageParts() {
      // Printing draws every page as one run.
      if (this.printAll) return this.reportPages.reduce((all, p) => all.concat(p), [])
      return this.reportPages[Math.min(this.page, this.reportPages.length - 1)] || []
    },
    isLastPage() {
      return this.printAll || this.page >= this.reportPages.length - 1
    },
    /**
     * footerNotes: [{text, whenAnyRow: {col: value | [values]}, placement}]:
     * FrameTracker's legend, printed once a flagged row prints. 'end' (the
     * default) prints after the report; 'pageFooterFromFirstMatch' prints in
     * the page footer (on screen: from the page holding the first match on).
     */
    footerNotes() {
      const notes = (this.layout && this.layout.footerNotes) || []
      return notes
        .map(note => {
          const test = note.whenAnyRow
          let first = test ? this.rows.findIndex(r => Object.keys(test).every(k => {
            const allowed = Array.isArray(test[k]) ? test[k] : [test[k]]
            return allowed.map(String).includes(String(r[k]))
          })) : 0
          return {
            text: renderTemplate(note.text || '', this.templateContext),
            placement: note.placement || 'end',
            first,
          }
        })
        .filter(note => note.first !== -1)
    },
    endNotes() {
      return this.footerNotes.filter(n => n.placement !== 'pageFooterFromFirstMatch')
    },
    /** Page-footer notes due on the screen page on view. */
    pageNotes() {
      const pageStart = this.printAll ? 0 : this.pageStartRow
      return this.footerNotes.filter(n => n.placement === 'pageFooterFromFirstMatch' &&
          (this.printAll || n.first < pageStart + this.pageRowCount))
    },
    pageStartRow() {
      let start = 0
      for (let p = 0; p < this.page && p < this.reportPages.length; p++) {
        this.reportPages[p].forEach(part => {
          start += part.rows.length
        })
      }
      return start
    },
    pageRowCount() {
      return this.pageParts.reduce((n, part) => n + part.rows.length, 0)
    },
  },
  methods: {
    ariaSort(col) {
      if (!this.sortable) return null
      if (this.sortKey !== col.key) return 'none'
      return this.sortDir === 'desc' ? 'descending' : 'ascending'
    },
    alignFor,
    formatCell,
    money,
    price,
    /**
     * Whether a hideTotalsWhen condition holds for the run on screen: any key
     * set ('*') or equal to its value. Keys may be reserved names
     * ('_scope:sls', '_meta:key').
     */
    hiddenBy(when) {
      if (!when) return false
      return Object.keys(when).some(k => {
        let value = this.ranWith[k]
        if (k.indexOf('_scope:') === 0) value = pathValue(this.meta || {}, 'scope.' + k.slice(7))
        else if (k.indexOf('_meta:') === 0) value = pathValue(this.meta || {}, k.slice(6))
        const set = value !== undefined && value !== null && value !== ''
        return when[k] === '*' ? set : set && String(value) === String(when[k])
      })
    },
    /** Whether the run on screen sent `value` for `name` (strings compared). */
    ranMatches(name, value) {
      const sent = this.ranWith[name]
      if (sent === undefined || sent === null) return value === '' || value === null
      return String(sent) === String(value)
    },
    showDays(row) {
      return Number(row.Days) !== this.layout.daysSentinel && Number(row.BalDue) !== 0
    },
    showPaid(row) {
      return Number(row.BalDue) === 0 && Number(row.Days) !== this.layout.daysSentinel
    },
    /**
     * One figure off a summary report's single row.
     *
     * The response shape is unchanged (`data` is still an array), so a summary
     * report is simply one row wide enough to hold every figure. Reading through
     * here rather than templating `rows[0][key]` keeps the empty-result case from
     * throwing if this ever renders before a run.
     */
    figure(key) {
      return this.hasResult ? this.rows[0][key] : null
    },
    /** A summary field formats like its column, or by its own `format`. */
    summaryColumn(field) {
      const column = this.columnsByKey[field.key] || {key: field.key, type: 'string'}
      return field.format ? {...column, format: field.format} : column
    },
    usDate(value) {
      if (!value) return ''
      return dateToStr(value)
    },
    /**
     * The bare marker some layouts print beside a row: rptCheckRegister's 'm',
     * meaning one check settled more than one invoice (ppvarMulCalc).
     */
    markerFor(row) {
      const marker = this.layout && this.layout.marker
      if (!marker) return ''
      const value = Number(row[marker.key])
      const matches = marker.when === 'gt'
          ? value > marker.value
          : row[marker.key] === marker.value
      return matches ? marker.label : ''
    },
    sum(rows, key) {
      return rows.reduce((total, row) => total + (parseFloat(row[key]) || 0), 0)
    },
    /**
     * A summed figure, per band (the ppDBCalc's own DisplayFormat and
     * BlankWhenZero): in a group footer footerFormat, then totalFormat, then
     * format, blank for zero with footerBlankWhenZero; in the summary band
     * totalFormat, then format, with totalBlankWhenZero. Without a format, by
     * type: a count is not money.
     */
    formatTotal(value, col, band = 'total') {
      const column = col || {}
      const blank = band === 'footer' ? column.footerBlankWhenZero : column.totalBlankWhenZero
      if (blank && Number(value) === 0) return ''
      const format = band === 'footer'
          ? (column.footerFormat || column.totalFormat || column.format)
          : (column.totalFormat || column.format)
      if (format) return formatWith(format, value)
      if (column.type === 'price') return this.price(value)
      if (column.type === 'int') {
        return (parseFloat(value) || 0).toLocaleString('en-US', {maximumFractionDigits: 2})
      }
      return this.money(value)
    },
    /**
     * A footer percent (ppvarPerc1Calc and kin):
     *   {cost, price}:   (1 - Σcost / Σprice) x 100
     *   {profit, total}: Σprofit / Σtotal x 100
     * computed whenever the divisor is non-zero, negative included, as
     * FrameTracker's calc does, and blank when it is zero. With `minProfit` the
     * percent is 0 unless the profit (Σprofit, or Σprice - Σcost) exceeds it,
     * FrameTracker's guard. Printed with the spec's
     * `format` (default whole percent and ' %').
     */
    margin(rows, spec) {
      let value
      if (spec.profit !== undefined) {
        const total = this.sum(rows, spec.total)
        if (total === 0) return ''
        const profit = this.sum(rows, spec.profit)
        value = spec.minProfit !== undefined && !(profit > spec.minProfit) ? 0 : profit / total * 100
      } else {
        const price = this.sum(rows, spec.price)
        if (price === 0) return ''
        const cost = this.sum(rows, spec.cost)
        value = spec.minProfit !== undefined && !(price - cost > spec.minProfit) ? 0 : (1 - cost / price) * 100
      }
      if (spec.format) return formatFloat(spec.format, value)
      return Math.round(value) + ' %'
    },
    countText(n, spec) {
      const number = spec.format ? formatFloat(spec.format, n) : n.toLocaleString('en-US')
      return [spec.label, number, spec.caption].filter(p => p !== '' && p !== null && p !== undefined).join(' ')
    },
    /**
     * A total row as cells: a caption, then a figure under each total column, a
     * margin under each margin column, blank elsewhere.
     *
     * The count (when `count` is given) goes under its `at` column, or else in the
     * first blank cell after the last figure, or a cell of its own past the last
     * column: where the legacy layouts print it. It counts `countRows` when given
     * (a split total still counts every row).
     */
    totalRow(rows, {caption = '', totals = [], count = null, countRows = null, margins = true, band = 'total', cells = null}) {
      const cols = this.reportColumns
      const totalSet = new Set(totals)
      const marginSpecs = margins ? this.totalMargins : {}
      const texts = cols.map(col => {
        // `cells`: a sum of another field printed under this column.
        if (cells && cells[col.key]) return this.footerCell(rows, cells[col.key], col, band)
        if (totalSet.has(col.key)) return this.formatTotal(this.sum(rows, col.key), this.columnsByKey[col.key] || col, band)
        if (marginSpecs[col.key]) return this.margin(rows, marginSpecs[col.key])
        return ''
      })

      let overflow = null
      let countAt = -1
      if (count) {
        const text = this.countText((countRows || rows).length, count)
        const at = count.at ? cols.findIndex(c => c.key === count.at) : -1
        if (at >= 0) {
          texts[at] = texts[at] ? texts[at] + ' ' + text : text
          countAt = at
        } else {
          let last = this.labelSpan - 1
          texts.forEach((t, i) => {
            if (t !== '' && i >= this.labelSpan) last = i
          })
          if (last + 1 < texts.length) {
            texts[last + 1] = text
            countAt = last + 1
          } else {
            overflow = text
          }
        }
      }

      const out = this.captionRow(caption, texts, countAt)
      if (overflow !== null) out.push({key: '__count', text: overflow, colspan: 1, cls: 'text-right'})
      return out
    },
    /**
     * Cells for a caption plus one text per printed column. The caption spans
     * the leading columns up to the first figure (at most labelSpan); a figure in
     * the very first column shares its cell with the caption.
     */
    captionRow(caption, texts, countAt = -1) {
      const cols = this.reportColumns
      let first = texts.findIndex(t => t !== '')
      if (first === -1) first = texts.length
      let span = Math.min(first, this.labelSpan)
      const cells = []
      if (span === 0) {
        cells.push({key: '__caption', text: [caption, texts[0]].filter(t => t).join(' '), colspan: 1, cls: this.alignFor(cols[0])})
        span = 1
      } else {
        cells.push({key: '__caption', text: caption, colspan: span, cls: ''})
      }
      for (let i = span; i < texts.length; i++) {
        // A count placed under a text column keeps that column's alignment.
        const cls = i === countAt && texts[i] !== '' ? this.alignFor(cols[i]) || '' : 'text-right'
        cells.push({key: cols[i].key, text: texts[i], colspan: 1, cls})
      }
      return cells
    },
    /**
     * A footer `cells` entry: {sum: key} prints the sum of that field under
     * this column (FrameTracker's ppDBCalc on a different field), formatted by
     * this column's band format, else the summed field's.
     */
    footerCell(rows, spec, col, band) {
      if (!spec || spec.sum === undefined) return this.summaryCell(spec, col, rows, band)
      const own = band === 'footer' ? (col.footerFormat || col.totalFormat || col.format) : (col.totalFormat || col.format)
      const column = own ? col : (this.columnsByKey[spec.sum] || col)
      return this.formatTotal(this.sum(rows, spec.sum), column, band)
    },
    /**
     * One cell of a summaryRows (or footerRows) line, over `rows`. A spec's own
     * `format` wins over the column's band format; `caption` follows the figure
     * ('12 Order(s)').
     */
    summaryCell(spec, col, rows = this.rows, band = 'total') {
      if (!spec) return ''
      const ctx = {...this.templateContext, rows, row: rows[0]}
      const caption = spec.caption ? renderTemplate(spec.caption, ctx) : ''
      // prefix / suffix wrap the figure; caption follows it after a space.
      const finish = text => {
        if (text === '') return text
        const wrapped = (spec.prefix || '') + text + (spec.suffix || '')
        return caption ? wrapped + ' ' + caption : wrapped
      }
      if (spec.text !== undefined) return finish(renderTemplate(String(spec.text), ctx))
      if (spec.ratio) return finish(this.ratioCell(rows, spec.ratio, col, band))
      if (spec.count) return finish(formatFloat(spec.format || '#,0', rows.length))
      if (spec.sum !== undefined) {
        let picked = rows
        if (spec.where) {
          picked = picked.filter(r => Object.keys(spec.where).every(k => {
            const allowed = Array.isArray(spec.where[k]) ? spec.where[k] : [spec.where[k]]
            return allowed.map(String).includes(String(r[k]))
          }))
        }
        if (spec.format) return finish(formatWith(spec.format, this.sum(picked, spec.sum)))
        return finish(this.footerCell(picked, spec, col, band))
      }
      if (spec.value !== undefined) {
        const v = pathValue({meta: this.meta || {}}, spec.value)
        if (v === undefined || v === null) return ''
        if (spec.format) return finish(formatWith(spec.format, v))
        return finish(col.totalFormat || col.format ? this.formatTotal(v, col, band) : String(v))
      }
      // A caption alone is a fixed caption in that column ('Current', '30 Days').
      return caption
    },
    /** A group's header line: the level's template, else the group's label. */
    groupHeader(group) {
      const level = group.level || {}
      const row = group.rows[0] || {}
      if (level.header !== undefined && level.header !== null) {
        return renderTemplate(level.header, {...this.templateContext, rows: group.rows, row})
      }
      const label = row[level.label]
      return label !== undefined && label !== null && label !== '' ? label : row[level.key]
    },
    /**
     * pageBreakPerGroup: a group (on groupBy or a level) that starts on a new
     * printed page. True for the first piece of a section whose own level or a
     * level opening with it asks for it.
     */
    breaksBefore(part) {
      if (!part.first || !part.section.level) return false
      return !!part.section.level.pageBreakPerGroup || part.section.opens.some(g => g.level && g.level.pageBreakPerGroup)
    },
    /** The columns a level's footer sums: its own, else groupTotals, else totals. */
    groupTotalKeys(level) {
      if (level && Array.isArray(level.totals)) return level.totals
      if (this.layout && Array.isArray(this.layout.groupTotals)) return this.layout.groupTotals
      return this.totalKeys
    },
    /** The count a level's footer prints, or null. `count` on the level overrides. */
    groupCountSpec(level) {
      if (level && level.count !== undefined) {
        if (!level.count) return null
        if (level.count === true) return this.countSpec
        if (typeof level.count === 'string') return {...this.countSpec, label: '', caption: level.count}
        return {label: '', caption: '', at: this.countSpec.at, format: this.countSpec.format, ...level.count}
      }
      return this.showCount ? this.countSpec : null
    },
    /**
     * Whether a group's normal footer line prints. Not when the level hides
     * its totals for this run, not below footerWhenCountAbove rows, and not for
     * a level declaring `totals: []` with no count or cells (its footerRows
     * stand alone).
     */
    footerShown(level, group = null) {
      if (!level || level.footer === false || this.hiddenBy(level.hideTotalsWhen)) return false
      if (group && level.footerWhenCountAbove !== undefined && !(group.rows.length > level.footerWhenCountAbove)) return false
      const count = this.groupCountSpec(level)
      if (Array.isArray(level.totals) && level.totals.length === 0 && !count && !level.cells) return false
      return this.groupTotalKeys(level).length > 0 || !!count || !!level.cells ||
          Object.keys(this.totalMargins).length > 0 || (level.footer !== undefined && level.footer !== null)
    },
    /** The groups whose footers print after this section: itself, then outer groups closing. */
    footerGroups(section) {
      return section.level ? [section, ...section.closes] : []
    },
    /**
     * A group's footer lines in order: the normal footer line and its
     * footerRows (before it with footerRowsPosition: 'before'). hideTotalsWhen
     * and footerWhenCountAbove hide both.
     */
    footerLines(group) {
      const level = group.level || {}
      if (this.hiddenBy(level.hideTotalsWhen)) return []
      if (level.footerWhenCountAbove !== undefined && !(group.rows.length > level.footerWhenCountAbove)) return []
      const main = this.footerShown(level, group) ? [this.groupFooterCells(group)] : []
      const rows = this.groupFooterRows(group)
      return level.footerRowsPosition === 'before' ? rows.concat(main) : main.concat(rows)
    },
    groupFooterCells(group) {
      const level = group.level || {}
      const caption = level.footer !== undefined && level.footer !== null
          ? renderTemplate(level.footer, {...this.templateContext, rows: group.rows, row: group.rows[0]})
          : (level.totalsCaption || this.layout.groupTotalsCaption || '')
      return this.totalRow(group.rows, {
        caption,
        totals: this.groupTotalKeys(level),
        count: this.groupCountSpec(level),
        band: 'footer',
        cells: level.cells || null,
      })
    },
    /**
     * footerRows: further footer lines of a group, shaped like summaryRows
     * ({label, cells}) and summed over the group's rows.
     */
    groupFooterRows(group) {
      const lines = (group.level && group.level.footerRows) || []
      return lines.map(line => {
        const texts = this.reportColumns.map(col => this.summaryCell((line.cells || {})[col.key], col, group.rows, 'footer'))
        const label = renderTemplate(line.label || '', {...this.templateContext, rows: group.rows, row: group.rows[0]})
        return this.captionRow(label, texts)
      })
    },
    sideAlign(table, col) {
      if (table.totals.includes(col.key) || NUMERIC_TYPES.includes(col.type)) return 'text-right'
      if (col.format && !/[dmy]/i.test(String(col.format).replace(/"[^"]*"|'[^']*'/g, ''))) return 'text-right'
      return ''
    },
    /**
     * A ratio cell: (Σnum / Σden [- 1]) x scale over the rows, capped at `cap`
     * when given. Blank when Σden is 0, or 0 in the cell's format with
     * whenDenZero: 'zero'.
     */
    ratioCell(rows, spec, col, band) {
      const den = this.sum(rows, spec.den)
      const format = spec.format || (band === 'footer' ? (col.footerFormat || col.totalFormat || col.format) : (col.totalFormat || col.format)) || '0.00'
      if (den === 0) return spec.whenDenZero === 'zero' ? formatWith(format, 0) : ''
      let value = this.sum(rows, spec.num) / den
      if (spec.minus1) value -= 1
      value *= spec.scale !== undefined ? spec.scale : 1
      if (spec.cap !== undefined && spec.cap !== null && value > spec.cap) value = spec.cap
      return formatWith(format, value)
    },
  },
  watch: {
    rows() {
      this.page = 0
    },
    reportPages: {
      immediate: true,
      handler(pages) {
        this.$emit('pages', {page: this.page, pages: pages.length})
      },
    },
    page(page) {
      this.$emit('pages', {page, pages: this.reportPages.length})
    },
  },
}
</script>

<style scoped>
/* Deliberately plain and dense, closer to a printed report than to a web table. */
.ft-report {
  background: #fff;
}

.ft-report-table {
  font-size: 0.85rem;
}

/*
 * The summary stack sits in the middle of the page rather than filling it, the
 * way the legacy form's panel of four numeric boxes does. Sized to the longest
 * label rather than to the viewport.
 */
.ft-summary {
  width: auto;
  min-width: 22rem;
}

.ft-summary td {
  border-top: none;
  padding: 0.35rem 1.5rem;
}

.ft-marker {
  margin-left: 0.4rem;
  font-weight: bold;
}

.ft-categories {
  margin-top: 1.5rem;
  border-top: 2px solid #343a40;
}

.ft-side {
  margin-top: 1.5rem;
}

.ft-side-title {
  font-weight: bold;
  margin-bottom: 0.3rem;
}

.ft-group-header td {
  font-weight: bold;
  background: #f4f6f9;
}

.ft-subtotal td,
.ft-total td {
  border-top: 1px solid #adb5bd;
  font-weight: bold;
}

.ft-total td {
  border-top: 2px solid #343a40;
}

/* The outer level of a nested grouping: rptDeliveryAcct's Location bands. */
.ft-group-outer td {
  background: #e9ecef;
}

.ft-subtotal-outer td {
  border-top: 1px solid #343a40;
}

/* footerNotes: after the report, or in the page footer. */
.ft-note {
  margin-top: 0.75rem;
}

.ft-page-note {
  border-top: 1px solid #dee2e6;
  margin-top: 0.5rem;
  padding-top: 0.25rem;
}

/* Report view headers sort on click (the runner sorts; see ReportRunner). */
.ft-report-table th.ft-sortable {
  cursor: pointer;
  user-select: none;
}
.ft-report-table th.ft-sortable:hover,
.ft-report-table th.ft-sortable:focus {
  text-decoration: underline;
}
.ft-sort-mark {
  font-size: .75em;
  margin-left: 3px;
}

@media print {
  .ft-report {
    padding: 0;
  }

  .ft-report-table thead {
    display: table-header-group;
  }

  .ft-report-table tr {
    break-inside: avoid;
  }

  /* pageBreakPerGroup. */
  .ft-break-before {
    break-before: page;
  }

  /* A fixed element repeats at the foot of every printed page. */
  .ft-page-note {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    border-top: none;
    background: #fff;
  }
}
</style>
